import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { preciseTimer, TimerState, TimerEvent } from '@/utils/PreciseTimer';
import { timerMetrics } from '@/utils/timerMetrics';
import { timerDebugger } from '@/utils/TimerDebugger';
import { useToast } from '@/hooks/use-toast';
import { sessionStateManager } from '@/lib/sessionStateManager';
import { crossTabSync } from '@/lib/crossTabSync';
import { supabase } from '@/integrations/supabase/client';
import { calculateRemainingSeconds } from '@/utils/sessionTimeUtils';
import type { Session } from '@/types/session';

// Enhanced interface with error handling and events
interface TimerContextType {
  // State
  isRunning: boolean;
  isPaused: boolean;
  remainingTime: number;
  elapsedTime: number;
  progress: number;
  formattedTime: string;
  sessionId: string | null;
  startTime: number | null;
  pauseTime: number | null;
  totalDuration: number;
  lastError: string | null;
  isError: boolean;
  
  // Database operation status
  isSaving: boolean;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSaveTime: number | null;
  
  // Actions
  startTimer: (duration: number, sessionId?: string) => void;
  pauseTimer: () => void;
  resumeTimer: () => void;
  stopTimer: () => void;
  resetTimer: () => void;
  preserveTimerState: () => void;
  restoreTimerState: (state: TimerState) => void;
  clearError: () => void;
  
  // Callbacks
  onComplete?: (sessionId: string | null) => void;
  setOnComplete: (callback: (sessionId: string | null) => void) => void;
  
  // Server time restoration
  restoreTimerWithServerTime: (sessionId: string, serverTimeState: any) => Promise<void>;
}

const TimerContext = createContext<TimerContextType | null>(null);

interface TimerProviderProps {
  children: React.ReactNode;
}

export function TimerProvider({ children }: TimerProviderProps) {
  // Track if this is the initial mount
  const isInitialMount = useRef(true);
  const lastLocalStorageWrite = useRef<number>(0);
  const localStorageWriteThrottle = 2000; // Write to localStorage max once per 2 seconds
  
  // Only log on actual mount, not re-renders
  useEffect(() => {
    if (isInitialMount.current) {
      console.log('🚀 TimerProvider: Component mounted', {
        timestamp: new Date().toISOString()
      });
      isInitialMount.current = false;
    }
  }, []);
  
  const { toast } = useToast();
  const [timerState, setTimerState] = useState<TimerState>({
    sessionId: null,
    totalDuration: 0,
    startTime: null,
    pauseTime: null,
    isRunning: false,
    isPaused: false,
    elapsedTime: 0,
    remainingTime: 0,
    progress: 0,
    formattedTime: '00:00',
    lastUpdate: Date.now()
  });
  
  const [lastError, setLastError] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);
  const [onComplete, setOnComplete] = useState<((sessionId: string | null) => void) | null>(null);
  
  // Database operation status
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSaveTime, setLastSaveTime] = useState<number | null>(null);
  
  // Use ref to track previous state to avoid stale closures in timer listener
  const previousStateRef = useRef<TimerState>(timerState);

  // Use state values from timer
  const remainingTime = timerState.remainingTime || 0;
  const elapsedTime = timerState.elapsedTime || 0;
  const progress = timerState.progress || 0;
  const formattedTime = timerState.formattedTime || '00:00';

  // Timer state restoration with database-first approach
  // Primary restoration should be handled by parent components using SessionStateManager
  // This provides fallback restoration for cases where parent components don't handle it
  useEffect(() => {
    console.log('🚀 TimerContext: Component mounted - checking for timer state restoration');
    console.log('🔍 TimerContext: Component mount state:', {
      hasTimerState: !!timerState,
      timerStateSessionId: timerState?.sessionId,
      timestamp: new Date().toISOString()
    });
    
    const restoreTimerState = async () => {
      try {
        const savedState = localStorage.getItem('deepfocus_timer_state');
        console.log('🔍 TimerContext: localStorage check result:', { 
          hasSavedState: !!savedState,
          timestamp: new Date().toISOString()
        });
        
        if (savedState) {
          const parsedState = JSON.parse(savedState);
          console.log('🔄 TimerContext: Found saved timer state:', parsedState);
          
          // Validate the saved state
          if (parsedState.sessionId && parsedState.totalDuration > 0) {
            // Validate totalDuration is reasonable (not corrupted)
            if (parsedState.totalDuration < 60 || parsedState.totalDuration > 14400) {
              console.error('❌ TimerContext: Invalid totalDuration detected, clearing corrupted state:', {
                totalDuration: parsedState.totalDuration,
                expectedRange: '60-14400 seconds'
              });
              localStorage.removeItem('deepfocus_timer_state');
              return;
            }
            
            // Check if the timer state is too old (more than 24 hours)
            const now = Date.now();
            const startTime = parsedState.startTime || now;
            const ageInHours = (now - startTime) / (1000 * 60 * 60);
            
            if (ageInHours > 24) {
              console.log('🕐 TimerContext: Timer state is too old (24+ hours), clearing:', {
                ageInHours: ageInHours.toFixed(2)
              });
              localStorage.removeItem('deepfocus_timer_state');
              return;
            }
            
            // CRITICAL: Validate session exists and is active in database before restoring
            try {
              const { data: session, error: sessionError } = await supabase
                .from('sessions')
                .select('id, status, user_id')
                .eq('id', parsedState.sessionId)
                .single();
              
              if (sessionError || !session) {
                console.log('❌ TimerContext: Session not found in database, clearing timer state:', {
                  sessionId: parsedState.sessionId,
                  error: sessionError?.message
                });
                localStorage.removeItem('deepfocus_timer_state');
                preciseTimer.stop();
                setTimerState({
                  sessionId: null,
                  totalDuration: 0,
                  startTime: null,
                  pauseTime: null,
                  isRunning: false,
                  isPaused: false,
                  elapsedTime: 0,
                  remainingTime: 0,
                  progress: 0,
                  formattedTime: '00:00',
                  lastUpdate: Date.now()
                });
                return;
              }
              
              // Check if session is still active or paused (not completed/cancelled)
              if (session.status !== 'active' && session.status !== 'paused') {
                console.log('❌ TimerContext: Session is not active, clearing timer state:', {
                  sessionId: parsedState.sessionId,
                  status: session.status
                });
                localStorage.removeItem('deepfocus_timer_state');
                preciseTimer.stop();
                setTimerState({
                  sessionId: null,
                  totalDuration: 0,
                  startTime: null,
                  pauseTime: null,
                  isRunning: false,
                  isPaused: false,
                  elapsedTime: 0,
                  remainingTime: 0,
                  progress: 0,
                  formattedTime: '00:00',
                  lastUpdate: Date.now()
                });
                return;
              }
              
              console.log('✅ TimerContext: Session validated in database:', {
                sessionId: session.id,
                status: session.status
              });
            } catch (dbError) {
              console.error('❌ TimerContext: Error validating session in database:', dbError);
              // On error, clear the timer state to be safe
              localStorage.removeItem('deepfocus_timer_state');
              preciseTimer.stop();
              setTimerState({
                sessionId: null,
                totalDuration: 0,
                startTime: null,
                pauseTime: null,
                isRunning: false,
                isPaused: false,
                elapsedTime: 0,
                remainingTime: 0,
                progress: 0,
                formattedTime: '00:00',
                lastUpdate: Date.now()
              });
              return;
            }
            
            // Restore timer state if it was running or paused
            if (parsedState.isRunning || parsedState.isPaused) {
              const currentElapsedTime = Math.floor((now - startTime) / 1000);
              const updatedState = {
                ...parsedState,
                elapsedTime: currentElapsedTime,
                remainingTime: Math.max(0, parsedState.totalDuration - currentElapsedTime),
                progress: Math.min(1, currentElapsedTime / parsedState.totalDuration),
                lastUpdate: now
              };
              
              console.log('✅ TimerContext: RESTORING timer state from localStorage (session validated):', updatedState);
              setTimerState(updatedState);
              
              // Use restoreState instead of start for better restoration
              console.log('🔄 TimerContext: Using preciseTimer.restoreState() for restoration');
              preciseTimer.restoreState(updatedState);
              console.log('✅ TimerContext: preciseTimer.restoreState() completed');
            } else {
              console.log('🔄 TimerContext: Timer was not running or paused, not restoring');
              setTimerState(parsedState);
            }
          } else {
            console.log('🔄 TimerContext: Invalid timer state, clearing:', parsedState);
            localStorage.removeItem('deepfocus_timer_state');
          }
        }
      } catch (error) {
        console.error('❌ TimerContext: Error restoring timer state:', error);
        localStorage.removeItem('deepfocus_timer_state');
      }
    };

    // Restore timer state immediately for fast UI update (no delay)
    restoreTimerState();
    
    // Also add a cleanup timeout to clear orphaned localStorage after restoration
    const cleanupTimeout = setTimeout(() => {
      // Only clear localStorage if we're sure no timer should be running
      const savedState = localStorage.getItem('deepfocus_timer_state');
      if (savedState) {
        try {
          const parsedState = JSON.parse(savedState);
          // Only clear if the timer state is clearly invalid or very old
          if (!parsedState.sessionId || parsedState.totalDuration <= 0) {
            console.log('🧹 TimerContext: Clearing invalid localStorage after restoration timeout');
            localStorage.removeItem('deepfocus_timer_state');
          }
        } catch (e) {
          console.log('🧹 TimerContext: Clearing corrupted localStorage after restoration timeout');
          localStorage.removeItem('deepfocus_timer_state');
        }
      }
    }, 2000); // Increased timeout to 2 seconds
    
    return () => {
      clearTimeout(cleanupTimeout);
    };
  }, []); // Only run once on mount

  // Set up timer state listener
  useEffect(() => {
    const unsubscribe = preciseTimer.addListener((newState) => {
      const oldState = previousStateRef.current;
      
      // Only log significant state changes to reduce console spam
      const hasSignificantChange = 
        oldState.isRunning !== newState.isRunning ||
        oldState.isPaused !== newState.isPaused ||
        Math.abs(oldState.remainingTime - newState.remainingTime) > 1 ||
        Math.abs(oldState.progress - newState.progress) > 0.1;
      
      if (hasSignificantChange) {
        console.log('🔄 TimerContext: Significant state change', {
          isRunning: newState.isRunning,
          isPaused: newState.isPaused,
          remainingTime: newState.remainingTime,
          formattedTime: newState.formattedTime,
          progress: newState.progress,
          sessionId: newState.sessionId
        });
      }
      
      // Monitor state changes (only in debug mode)
      if (import.meta.env.DEV) {
        timerDebugger.monitorStateChange(oldState, newState, newState.sessionId);
      }
      
      // Persist timer state to localStorage for page reload recovery
      // Throttle writes to avoid performance issues (max once per 2 seconds)
      const now = Date.now();
      const shouldWriteToStorage = 
        (now - lastLocalStorageWrite.current) >= localStorageWriteThrottle ||
        oldState.isRunning !== newState.isRunning ||
        oldState.isPaused !== newState.isPaused;
      
      try {
        if (newState.sessionId && (newState.isRunning || newState.isPaused)) {
          if (shouldWriteToStorage) {
            const stateToSave = {
              ...newState,
              startTime: newState.startTime || Date.now(),
              lastUpdate: Date.now()
            };
            localStorage.setItem('deepfocus_timer_state', JSON.stringify(stateToSave));
            lastLocalStorageWrite.current = now;
            
            // Only log in development mode
            if (import.meta.env.DEV && hasSignificantChange) {
              console.log('💾 TimerContext: Saved timer state to localStorage');
            }
          }
        } else if (!newState.isRunning && !newState.isPaused) {
          // Clear localStorage when timer is stopped (always do this immediately)
          localStorage.removeItem('deepfocus_timer_state');
          lastLocalStorageWrite.current = now;
          if (import.meta.env.DEV) {
            console.log('🧹 TimerContext: Cleared timer state from localStorage (timer stopped)');
          }
        }
      } catch (error) {
        console.error('❌ TimerContext: Failed to persist timer state:', error);
      }
      
      // Update state and ref
      previousStateRef.current = newState;
      setTimerState(newState);
    });

    // Set up timer event listener
    // UX: Timer visual state changes provide immediate feedback, so we only
    // show toasts for completion (milestone) and errors (need user attention)
    const unsubscribeEvents = preciseTimer.addEventListener((event: TimerEvent) => {
      console.log('🎯 TimerContext: Event received', event);

      switch (event.type) {
        case 'start':
          // No toast - timer UI provides immediate visual feedback
          setLastError(null);
          setIsError(false);
          break;

        case 'pause':
          // No toast - paused state is visually obvious from timer UI
          break;

        case 'resume':
          // No toast - running state is visually obvious from timer UI
          break;

        case 'complete':
          // Show completion toast - this is a milestone worth celebrating
          toast({
            title: "Session Complete!",
            description: "Great work on your focus session.",
            duration: 5000,
          });
          // CRITICAL: Ensure timer is stopped when completion event fires
          // This prevents timer from continuing to run if completion handler fails
          if (timerState.isRunning) {
            console.warn('⚠️ TimerContext: Timer still running on complete event, forcing stop');
            preciseTimer.stop();
          }
          // Call completion callback if set
          if (onComplete) {
            try {
              onComplete(event.sessionId);
            } catch (error) {
              console.error('❌ TimerContext: Error in completion callback:', error);
              // Timer is already stopped, so we can continue
            }
          }
          break;

        case 'error': {
          // Always show error toasts - user needs to know something went wrong
          const errorMessage = event.data?.message || 'Timer error occurred';
          setLastError(errorMessage);
          setIsError(true);
          toast({
            title: "Timer error",
            description: errorMessage,
            variant: "destructive",
            duration: 5000,
          });
          break;
        }
      }
    });

    // Set up cross-tab synchronization
    const unsubscribeCrossTab = crossTabSync.subscribeToTimerEvents((event) => {
      console.log('🔄 TimerContext: Received cross-tab timer event:', event);

      // Only process events for the current session
      if (event.sessionId === timerState.sessionId) {
        switch (event.type) {
          case 'timer_started':
            // Another tab started a timer - sync state
            console.log('🔄 TimerContext: Syncing timer start from another tab');
            break;
          case 'timer_paused':
            // Another tab paused the timer - sync state
            console.log('🔄 TimerContext: Syncing timer pause from another tab');
            preciseTimer.pause();
            break;
          case 'timer_resumed':
            // Another tab resumed the timer - sync state
            console.log('🔄 TimerContext: Syncing timer resume from another tab');
            preciseTimer.resume();
            break;
          case 'timer_stopped':
            // Another tab stopped the timer - sync state
            console.log('🔄 TimerContext: Syncing timer stop from another tab, stopping preciseTimer');
            preciseTimer.stop();
            localStorage.removeItem('deepfocus_timer_state');
            break;
          case 'timer_completed':
            // Another tab completed the timer - sync state
            console.log('🔄 TimerContext: Syncing timer completion from another tab, stopping preciseTimer');
            preciseTimer.stop();
            localStorage.removeItem('deepfocus_timer_state');
            break;
        }
      }
    });

    // Cleanup on unmount
    return () => {
      unsubscribe();
      unsubscribeEvents();
      unsubscribeCrossTab();
    };
  }, [toast]); // Removed timerState from dependencies to prevent infinite re-render loop

  // Delegate timer state persistence to SessionStateManager
  // TimerContext no longer handles localStorage directly - this is now managed by SessionStateManager
  useEffect(() => {
    console.log('🔄 TimerContext: Timer state changed, delegating persistence to SessionStateManager:', {
      sessionId: timerState.sessionId,
      totalDuration: timerState.totalDuration,
      isRunning: timerState.isRunning,
      timestamp: new Date().toISOString()
    });
    
    // SessionStateManager will handle persistence based on database-first architecture
    // TimerContext focuses only on timer mechanics and UI state
  }, [timerState]);

  // Update database session status when timer state changes
  // Note: This will be called by parent components that have access to userId
  // For now, we'll just log that status update should happen
  useEffect(() => {
    if (timerState.sessionId && (timerState.isRunning || timerState.isPaused)) {
      console.log('🔄 TimerContext: Session status update needed - should be handled by parent component');
    }
  }, [timerState.sessionId, timerState.isRunning, timerState.isPaused]);

  // Update session status in database
  const updateSessionStatus = useCallback(async (userId?: string) => {
    if (!timerState.sessionId || !userId) return;

    try {
      setIsSaving(true);
      setSaveStatus('saving');

      const status = timerState.isPaused ? 'paused' : 'active';
      await sessionStateManager.updateSessionStatus(timerState.sessionId, status, userId);

      setSaveStatus('saved');
      setLastSaveTime(Date.now());
      
      // Clear saved status after 2 seconds
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('Failed to update session status:', error);
      setSaveStatus('error');
      setLastError('Failed to save session status');
      setIsError(true);
    } finally {
      setIsSaving(false);
    }
  }, [timerState.sessionId, timerState.isRunning, timerState.isPaused]);

  // Restore timer state from SessionStateManager on mount
  useEffect(() => {
    const restoreFromSessionManager = async () => {
      try {
        // This will be called by the parent component that has access to userId
        // For now, we'll just log that restoration should happen
        console.log('🔄 TimerContext: Timer state restoration should be handled by parent component');
      } catch (e) {
        console.warn('Failed to restore timer state from SessionStateManager', e);
      }
    };

    restoreFromSessionManager();
  }, []); // Only run on mount

  // Function to restore timer state with server-calculated time
  // This is now the primary method for timer restoration in the database-first architecture
  const restoreTimerWithServerTime = useCallback(async (sessionId: string, serverTimeState: any) => {
    try {
      console.log('🔄 TimerContext: Restoring timer with server time state:', serverTimeState);
      
      if (!serverTimeState) {
        console.warn('No server time state provided, skipping restoration');
        return;
      }

      const totalDurationSeconds: number =
        typeof serverTimeState.total_duration === 'number'
          ? serverTimeState.total_duration
          : typeof serverTimeState.duration === 'number'
            ? serverTimeState.duration * 60
            : 0;

      const durationMinutes = totalDurationSeconds > 0 ? Math.ceil(totalDurationSeconds / 60) : 0;

      const status: Session['status'] = serverTimeState.status;
      const startedAt: string | null = serverTimeState.started_at ?? null;
      const pausedAt: string | null = serverTimeState.paused_at ?? null;

      const pseudoSession: Session = {
        id: sessionId,
        user_id: serverTimeState.user_id ?? 'unknown',
        goal: serverTimeState.goal ?? 'Focus Session',
        duration: durationMinutes,
        status,
        invite_code: null,
        started_at: startedAt,
        paused_at: pausedAt,
        completed_at: null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const remainingTime = calculateRemainingSeconds(pseudoSession);
      const elapsedTime = Math.max(0, totalDurationSeconds - remainingTime);
      const progress = totalDurationSeconds > 0 ? Math.min(100, (elapsedTime / totalDurationSeconds) * 100) : 0;

      const startedAtMs = startedAt ? new Date(startedAt).getTime() : null;
      const pausedAtMs = pausedAt ? new Date(pausedAt).getTime() : null;

      // CRITICAL: For paused sessions, shift the timer startTime forward so that
      // `Date.now() - startTime` equals elapsed-before-pause (prevents the timer
      // from being treated as completed while paused).
      const timerStartTime =
        status === 'paused' && startedAtMs && pausedAtMs && pausedAtMs >= startedAtMs
          ? startedAtMs + (Date.now() - pausedAtMs)
          : startedAtMs;

      const timerPauseTime = status === 'paused' ? Date.now() : null;

      // CRITICAL: If the timer has expired (remainingTime <= 0), do NOT restore as running.
      // This prevents "stuck" sessions showing as Active at 100% on the dashboard.
      const isExpired = remainingTime <= 0 && status === 'active';

      // Create timer state from timestamps (single source of truth)
      const timerState: TimerState = {
        sessionId: sessionId,
        totalDuration: totalDurationSeconds,
        startTime: timerStartTime,
        pauseTime: timerPauseTime,
        isRunning: isExpired ? false : status === 'active',
        isPaused: isExpired ? false : status === 'paused',
        elapsedTime: isExpired ? totalDurationSeconds : elapsedTime,
        remainingTime: isExpired ? 0 : remainingTime,
        progress: isExpired ? 100 : progress,
        formattedTime: isExpired ? preciseTimer.formatTime(0) : preciseTimer.formatTime(remainingTime),
        lastUpdate: Date.now()
      };

      if (isExpired) {
        console.log('⏰ TimerContext: Session has expired, restoring as completed (not running):', timerState);
      } else {
        console.log('🔄 TimerContext: Restoring timer state from server:', timerState);
      }

      preciseTimer.restoreState(timerState);

      // Update local state to reflect the restored timer
      setTimerState(timerState);
      
    } catch (error) {
      console.error('Failed to restore timer with server time:', error);
    }
  }, []);

  // Timer actions with error handling
  const startTimer = useCallback((duration: number, sessionId?: string) => {
    try {
      console.log('🚨 TimerContext: startTimer() called', { 
        duration, 
        sessionId, 
        timestamp: new Date().toISOString(),
        callStack: new Error().stack?.split('\n').slice(1, 4).join('\n')
      });
      
      if (duration <= 0) {
        throw new Error('Invalid duration');
      }
      
      const id = sessionId || 'default';
      
      // DEBUG: Log current timer state before starting
      console.log('🔍 TimerContext: Current state before startTimer:', {
        isRunning: timerState.isRunning,
        elapsedTime: timerState.elapsedTime,
        remainingTime: timerState.remainingTime,
        sessionId: timerState.sessionId
      });
      
      // Session state management is now handled by SessionStateManager
      // TimerContext focuses only on timer mechanics
      
      // Check if timer is already running for the same session
      if (timerState.isRunning && timerState.sessionId === id) {
        console.log('⚠️ Timer already running for session', id, 'skipping startTimer call');
        return;
      }
      
      console.log('🎯 TimerContext: Proceeding with preciseTimer.start()', { duration, id });
      preciseTimer.start(duration, id);
      timerMetrics.startSession(id);
      timerDebugger.monitorTimerOperation('start', id, { duration });
      
      // Broadcast timer start to other tabs
      crossTabSync.broadcast({
        type: 'timer_started',
        sessionId: id,
        userId: 'current-user', // This will be updated by the parent component
        data: { duration, timestamp: new Date().toISOString() }
      });
      
      setLastError(null);
      setIsError(false);
      
      // DEBUG: Log state after starting
      console.log('🔍 TimerContext: State after startTimer:', {
        isRunning: timerState.isRunning,
        elapsedTime: timerState.elapsedTime,
        remainingTime: timerState.remainingTime,
        sessionId: timerState.sessionId
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to start timer';
      setLastError(errorMessage);
      setIsError(true);
      console.error('Timer start error:', error);
    }
  }, [timerState.isRunning, timerState.elapsedTime, timerState.remainingTime, timerState.sessionId]);

  const pauseTimer = useCallback(async () => {
    try {
      console.log('⏸️ TimerContext: Pausing timer');
      preciseTimer.pause();
      timerMetrics.pause();
      timerDebugger.monitorTimerOperation('pause', timerState.sessionId);
      
      // Insert pause record to database
      if (timerState.sessionId) {
        try {
          const { error: pauseError } = await supabase
            .from('session_pauses')
            .insert({
              session_id: timerState.sessionId,
              paused_at: new Date().toISOString()
            });

          if (pauseError) {
            console.warn('Failed to insert pause record:', pauseError);
            // Don't block pause UI - continue even if DB insert fails
          }
        } catch (dbError) {
          console.warn('Error inserting pause record:', dbError);
          // Don't block pause UI - continue even if DB insert fails
        }
        
        // Broadcast timer pause to other tabs
        crossTabSync.broadcast({
          type: 'timer_paused',
          sessionId: timerState.sessionId,
          userId: 'current-user', // This will be updated by the parent component
          data: { timestamp: new Date().toISOString() }
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to pause timer';
      setLastError(errorMessage);
      setIsError(true);
      console.error('Timer pause error:', error);
    }
  }, [timerState.sessionId]);

  const resumeTimer = useCallback(async () => {
    try {
      console.log('▶️ TimerContext: Resuming timer');
      preciseTimer.resume();
      timerMetrics.resume();
      timerDebugger.monitorTimerOperation('resume', timerState.sessionId);
      
      // Update most recent pause record with resumed_at timestamp
      if (timerState.sessionId) {
        try {
          const { error: resumeError } = await supabase
            .from('session_pauses')
            .update({ resumed_at: new Date().toISOString() })
            .eq('session_id', timerState.sessionId)
            .is('resumed_at', null)
            .order('paused_at', { ascending: false })
            .limit(1);

          if (resumeError) {
            console.warn('Failed to update pause record:', resumeError);
            // Don't block resume UI - continue even if DB update fails
          }
        } catch (dbError) {
          console.warn('Error updating pause record:', dbError);
          // Don't block resume UI - continue even if DB update fails
        }
        
        // Broadcast timer resume to other tabs
        crossTabSync.broadcast({
          type: 'timer_resumed',
          sessionId: timerState.sessionId,
          userId: 'current-user', // This will be updated by the parent component
          data: { timestamp: new Date().toISOString() }
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to resume timer';
      setLastError(errorMessage);
      setIsError(true);
      console.error('Timer resume error:', error);
    }
  }, [timerState.sessionId]);

  const stopTimer = useCallback(() => {
    try {
      console.log('🛑 TimerContext: Stopping timer');
      preciseTimer.stop();
      timerMetrics.endSession();
      timerDebugger.monitorTimerOperation('stop', timerState.sessionId);
      
      // Broadcast timer stop to other tabs
      if (timerState.sessionId) {
        crossTabSync.broadcast({
          type: 'timer_stopped',
          sessionId: timerState.sessionId,
          userId: 'current-user', // This will be updated by the parent component
          data: { timestamp: new Date().toISOString() }
        });
      }
      
      setLastError(null);
      setIsError(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to stop timer';
      setLastError(errorMessage);
      setIsError(true);
      console.error('Timer stop error:', error);
    }
  }, [timerState.sessionId]);

  const resetTimer = useCallback(() => {
    try {
      console.log('🔄 TimerContext: Resetting timer');
      preciseTimer.stop();
      timerMetrics.endSession();
      timerDebugger.monitorTimerOperation('reset', timerState.sessionId);
      
      // Broadcast timer stop to other tabs (reset is essentially a stop)
      if (timerState.sessionId) {
        crossTabSync.broadcast({
          type: 'timer_stopped',
          sessionId: timerState.sessionId,
          userId: 'current-user', // This will be updated by the parent component
          data: { timestamp: new Date().toISOString(), action: 'reset' }
        });
      }
      
      setLastError(null);
      setIsError(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset timer';
      setLastError(errorMessage);
      setIsError(true);
      console.error('Timer reset error:', error);
    }
  }, [timerState.sessionId]);

  const preserveTimerState = useCallback(() => {
    try {
      const preservedState = preciseTimer.preserveState();
      console.log('💾 TimerContext: State preserved', preservedState);
    } catch (error) {
      console.error('Failed to preserve timer state:', error);
    }
  }, []);

  const restoreTimerState = useCallback((state: TimerState) => {
    try {
      preciseTimer.restoreState(state);
      console.log('🔄 TimerContext: State restored', state);
    } catch (error) {
      console.error('Failed to restore timer state:', error);
    }
  }, []);

  const clearError = useCallback(() => {
    setLastError(null);
    setIsError(false);
  }, []);

  // Note: hasValidTimerState function removed - session validation now handled by SessionStateManager

  // Set completion callback
  const setOnCompleteCallback = useCallback((callback: (sessionId: string | null) => void) => {
    setOnComplete(() => callback);
  }, []);

  // Auto-preserve state on navigation
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (timerState.isRunning || timerState.isPaused) {
        preserveTimerState();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [timerState.isRunning, timerState.isPaused, preserveTimerState]);

  const contextValue: TimerContextType = {
    // State
    isRunning: timerState.isRunning,
    isPaused: timerState.isPaused,
    remainingTime,
    elapsedTime,
    progress,
    formattedTime,
    sessionId: timerState.sessionId,
    startTime: timerState.startTime,
    pauseTime: timerState.pauseTime,
    totalDuration: timerState.totalDuration,
    lastError,
    isError,
    
    // Database operation status
    isSaving,
    saveStatus,
    lastSaveTime,
    
    // Actions
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    resetTimer,
    preserveTimerState,
    restoreTimerState,
    clearError,
    
    // Callbacks
    onComplete,
    setOnComplete: setOnCompleteCallback,
    
    // Server time restoration
    restoreTimerWithServerTime,
  };

  return (
    <TimerContext.Provider value={contextValue}>
      {children}
    </TimerContext.Provider>
  );
}

export function useTimerContext() {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error('useTimerContext must be used within a TimerProvider');
  }
  return context;
} 
